import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  RiAddLine,
  RiUserAddLine,
  RiFileTextLine,
  RiCheckboxCircleLine,
  RiSurveyLine,
  RiTaskLine,
} from '@remixicon/react'
import type { TimelineEvent } from 'shared'
import { formatDateTime } from '../lib/dates'
import { cx, focusRing, neutralTile } from '../lib/utils'

/**
 * A tone names what an event *is*, not the colour it happens to be. The brand has one
 * colour of structure, so survey, report and task share the navy tile and the icon says
 * which; `done` and `neutral` are the two that mean something rather than naming something.
 */
type Tone = 'survey' | 'report' | 'task' | 'done' | 'neutral'

const EVENT_STYLES: Record<string, { icon: typeof RiAddLine; tone: Tone }> = {
  workspace_created: { icon: RiAddLine, tone: 'neutral' },
  member_joined: { icon: RiUserAddLine, tone: 'neutral' },
  survey_published: { icon: RiSurveyLine, tone: 'survey' },
  survey_submitted: { icon: RiCheckboxCircleLine, tone: 'done' },
  survey_reopened: { icon: RiSurveyLine, tone: 'survey' },
  survey_closed: { icon: RiCheckboxCircleLine, tone: 'neutral' },
  artifact_published: { icon: RiFileTextLine, tone: 'report' },
  artifact_updated: { icon: RiFileTextLine, tone: 'report' },
  task_created: { icon: RiTaskLine, tone: 'task' },
  task_completed: { icon: RiCheckboxCircleLine, tone: 'done' },
}

const TONE_CLASSES: Record<Tone, string> = {
  survey: neutralTile,
  report: neutralTile,
  task: neutralTile,
  done: 'bg-emerald-100 text-emerald-700 ring-emerald-100 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-emerald-400/20',
  neutral: 'bg-gray-100 text-gray-500 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700',
}

interface TimelineProps {
  events: TimelineEvent[]
  /**
   * Where an event leads, when it leads anywhere. Returning null keeps the
   * entry as plain text — the operator console has nowhere to send a
   * `member_joined`, and the member side has nowhere to send a draft.
   */
  hrefFor?: (event: TimelineEvent) => string | null
}

export default function Timeline({ events, hrefFor }: TimelineProps) {
  const { t, i18n } = useTranslation()

  return (
    <ol className="relative space-y-1">
      {events.map((event, index) => {
        const style = EVENT_STYLES[event.type] ?? { icon: RiAddLine, tone: 'neutral' as Tone }
        const Icon = style.icon
        const isLast = index === events.length - 1
        const href = hrefFor?.(event) ?? null

        return (
          <li key={event.id} className="relative flex gap-4 pb-6 last:pb-0">
            {!isLast && (
              <span
                className="absolute left-4 top-9 -ml-px h-full w-px bg-gray-200 dark:bg-gray-800"
                aria-hidden="true"
              />
            )}

            <span
              className={cx(
                'relative flex size-8 shrink-0 items-center justify-center rounded ring-1',
                TONE_CLASSES[style.tone],
              )}
            >
              <Icon className="size-4" aria-hidden="true" />
            </span>

            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-sm text-gray-900 dark:text-gray-50">
                <span className="font-medium">{t(`timeline.${event.type}`, event.type)}</span>
                {' — '}
                {href ? (
                  <Link
                    to={href}
                    className={cx(
                      'rounded text-primary-600 underline-offset-2 hover:underline dark:text-primary-400',
                      focusRing,
                    )}
                  >
                    {event.title}
                  </Link>
                ) : (
                  <span className="text-gray-600 dark:text-gray-300">{event.title}</span>
                )}
              </p>
              {event.summary && (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{event.summary}</p>
              )}
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {formatDateTime(event.created_at, i18n.language)}
                {event.actor_name && event.actor_name !== event.title && ` · ${event.actor_name}`}
                {event.actor_type === 'agent' && ` · ${t('timeline.byAgent')}`}
              </p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
